import Skeleton from "react-loading-skeleton";
import { handleCreateCertificate } from "../../utils/handleCreateCertificate";
import { SelectFieldComponent } from "../../../../common/components/SelectFieldComponent";
import { getQualificationOptions } from "../../../../common/utils/selectOptions/getQualificationOptions";
import {
    setFormValues,
    setIsPostCreateCertificateSuccess,
} from "../../CreateCertificateModalSlice";
import { QualificationInterface } from "../../../../common/interfaces/QualificationInterface";
import { getEducatorOptions } from "../../../../common/utils/selectOptions/getEducatorOptions";
import { getEmployeeOptions } from "../../../../common/utils/selectOptions/getEmployeeOptions";
import { DateInputFieldComponent } from "../FormComponents/DateInputFieldComponent";
import { ChangeEvent, JSX, useEffect, useState } from "react";
import { FileInputFieldComponent } from "../FormComponents/FileInputFieldComponent";
import { SubmitButtonComponent } from "../FormComponents/SubmitButtonComponent";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useCreateCertificateMutation } from "../../../../common/api/certificate";
import { useGetQualificationsQuery } from "../../../../common/api/qualification";
import { useGetEmployeesQuery } from "../../../../common/api/employee";
import { useEstablishmentId } from "../../../../common/utils/useEstablishmentId";
import { StatusEnum } from "../../../../common/enums/StatusEnum";
import { useGetEducatorsQuery } from "../../../../common/api/educator";
import { QualificationTypeEnum } from "../../../../common/enums/QualificationTypeEnum";

export const PersonsCertificateForm = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const [file, setFile] = useState<File | null>(null);
    const { formValues } = useAppSelector(
        (state) => state.createCertificateModal,
    );

    const [
        postCreateCertificate,
        {
            isLoading: isPostCreateCertificateLoading,
            isSuccess: isPostCreateCertificateSuccess,
        },
    ] = useCreateCertificateMutation();

    const { data: qualifications } = useGetQualificationsQuery({
        items_per_page: 9999,
        filter: {
            type: QualificationTypeEnum.PERSON,
        },
    });

    const { data: employees, isLoading: isEmployeesLoading } =
        useGetEmployeesQuery({
            items_per_page: 9999,
            filter: {
                establishment_id: useEstablishmentId(),
                status: StatusEnum.ACTIVE,
            },
        });
    const { data: educatorData, isFetching: educatorDataIsFetching } =
        useGetEducatorsQuery({
            items_per_page: 9999,
            filter: {
                qualification_id: formValues?.qualification?.selectedId,
            },
        });

    useEffect(() => {
        if (!isPostCreateCertificateSuccess) {
            dispatch(setIsPostCreateCertificateSuccess(false));
        }
    }, [isPostCreateCertificateSuccess, dispatch]);

    if (isEmployeesLoading) {
        return <Skeleton count={5} borderRadius={"0.8rem"} height={"5.5rem"} />;
    }

    return (
        <form
            className={"c-popup__content__body"}
            onSubmit={(event) => {
                event.preventDefault();
                if (!isPostCreateCertificateLoading) {
                    handleCreateCertificate({
                        formValues,
                        file,
                        postCreateCertificate,
                        dispatch,
                    });
                }
            }}
        >
            <SelectFieldComponent
                id={1}
                options={getQualificationOptions(qualifications?.data)}
                onChange={(newValue) => {
                    const selectedQualification = qualifications?.data?.find(
                        (item: QualificationInterface) =>
                            item.id.toString() === newValue.value,
                    );

                    dispatch(
                        setFormValues({
                            ...formValues,
                            qualification: {
                                ...formValues?.qualification,
                                selectedId: newValue.value,
                                selectedValue: newValue.label,
                                validityPeriod:
                                    selectedQualification?.validity_period ??
                                    null,
                                validityType:
                                    selectedQualification?.validity_type ??
                                    null,
                                fixedValidUntil:
                                    selectedQualification?.fixed_valid_until ??
                                    null,
                            },
                        }),
                    );
                }}
                value={{
                    value: formValues?.qualification?.selectedId || "",
                    label: formValues?.qualification?.selectedValue || "",
                }}
                errorMessage={"Kwalificatie is verplicht"}
                placeHolder={"Zoeken..."}
                title={"Selecteer kwalificatie"}
                showValidityPeriod={true}
                validityPeriod={formValues?.qualification?.validityPeriod}
                validityType={formValues?.qualification?.validityType}
                fixedValidUntil={formValues?.qualification?.fixedValidUntil}
                showNotice={!formValues?.qualification?.selectedId}
                noticeText={
                    'Staat jouw kwalificatie niet tussen de resultaten? Neem contact op met onze servicedesk via <a href="tel:09006611111">0900 - 66 11 11 1</a>'
                }
            />
            <SelectFieldComponent
                id={2}
                options={getEducatorOptions(educatorData?.data)}
                onChange={(newValue) => {
                    dispatch(
                        setFormValues({
                            ...formValues,
                            educator: {
                                selectedId: newValue.value,
                                selectedValue: newValue.label,
                            },
                        }),
                    );
                }}
                value={{
                    value: formValues?.educator?.selectedId || "",
                    label: formValues?.educator?.selectedValue || "",
                }}
                errorMessage={"Opleider is verplicht"}
                placeHolder={"Selecteer opleider..."}
                title={"Selecteer opleider"}
                isLoading={educatorDataIsFetching}
            />
            <SelectFieldComponent
                id={3}
                options={getEmployeeOptions(employees?.data)}
                onChange={(newValue) => {
                    dispatch(
                        setFormValues({
                            ...formValues,
                            employee: {
                                ...formValues?.employee,
                                selectedId: newValue.value,
                                selectedValue: newValue.label,
                            },
                        }),
                    );
                }}
                value={{
                    value: formValues?.employee?.selectedId || "",
                    label: formValues?.employee?.selectedValue || "",
                }}
                errorMessage={"Medewerker is verplicht"}
                placeHolder={"Zoeken..."}
                title={"Selecteer medewerker"}
            />
            <DateInputFieldComponent
                id={4}
                value={formValues?.validAt}
                expiresAt={formValues?.expiresAt}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    dispatch(
                        setFormValues({
                            ...formValues,
                            validAt: event.target.value,
                        }),
                    )
                }
            />
            <FileInputFieldComponent
                id={5}
                formValues={formValues}
                dispatch={dispatch}
                setFile={setFile}
            />
            <SubmitButtonComponent
                isPostCreateCertificateLoading={isPostCreateCertificateLoading}
            />
        </form>
    );
};
